// src/pages/questionaries/nom035Questionnaire.jsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import nomQuestions from '../../data/nomQuestions';
import questionnaireScopeApi from '../../api/questionnaireScopeApi';
import questionnaireApplicationsApi from '../../api/questionnaireApplicationsApi';
import responsesApi from '../../api/responsesApi';

import './Nom035Questionnaire.css';
import DropdownQuestion from "../../components/questions/dropdownQuestion/dropdownQuestion";
import TextQuestion from "../../components/questions/textQuestion/textQuestion";

const PAGE_SIZE = 5;

export default function Nom035Questionnaire() {
  const { idEmpresa, questionnaire, applicationId } = useParams();
  const navigate = useNavigate();

  const questionsRef = useRef([]);
  const [answers, setAnswers] = useState({});
  const [companyName, setCompanyName] = useState('');
  const [companyJobAreas, setCompanyJobAreas] = useState([]);
  const [usesEmployeeCredentials, setUsesEmployeeCredentials] = useState(false);
  const [validatingEmployee, setValidatingEmployee] = useState(false);
  const [configurationLoaded, setConfigurationLoaded] = useState(false);

  const credentialQuestions = useMemo(() => ([
    {
      Component: TextQuestion,
      question: "Número de empleado",
      field: "employee_number",
      required: true,
    },
    {
      Component: TextQuestion,
      question: "Código de empleado",
      field: "employee_code",
      required: true,
    },
  ]), []);

  // 1) Defino aquí la pregunta extra
  const jobAreaQuestion = useMemo(() => ({
    Component: DropdownQuestion,
    question: "Área de trabajo",
    options: companyJobAreas,
    field: "jobArea",
    required: true
  }), [companyJobAreas]);

  // 2) Arreglo fusionado con la nueva pregunta en la posición 3
  const mergedQuestions = useMemo(() => {
    const arr = usesEmployeeCredentials
      ? [...credentialQuestions, ...nomQuestions.filter((question) => question.field !== "email")]
      : [...nomQuestions];
    arr.splice(usesEmployeeCredentials ? 4 : 3, 0, jobAreaQuestion);
    return arr;
  }, [credentialQuestions, jobAreaQuestion, usesEmployeeCredentials]);

  // 3) Estado para llevar track de respuestas de cada pregunta
  const [answeredState, setAnsweredState] = useState(
    () => new Array(mergedQuestions.length).fill(false)
  );

  // Section I keys (ahora basadas en mergedQuestions.fields)
  const secIKeys = [
    'seccionI_accidente',
    'seccionI_asaltos',
    'seccionI_actosViolentos',
    'seccionI_secuestro',
    'seccionI_amenazas',
    'seccionI_otroRiesgo'
  ];

  const allSecIAnswered = secIKeys.every(key => answers[key] != null && answers[key] !== '');
  const allSecINo = allSecIAnswered && secIKeys.every(key => answers[key] === 'No');

  // 4) Preguntas efectivas según sección I
const bloque13Key = 'bloque13_attention';   // campo de la ABQuestion del bloque 13
const bloque14Key = 'bloque14_supervisor'; // campo de la ABQuestion del bloque 14

const effectiveQuestions = useMemo(() => {
  // parte original: si en Sección I todas fueron “No”…
  let qs = [];
  if (allSecINo) {
    qs = mergedQuestions.filter(q =>
      !q.field.startsWith('seccionII_') &&
      !q.field.startsWith('seccionIII_') &&
      !q.field.startsWith('seccionIV_')
    );
  } else {
    qs = [...mergedQuestions];
  }

  // ocultar bloque 13 completo si la respuesta AB de ese bloque es “No”
  if (answers[bloque13Key] === 'No') {
    qs = qs.filter(q => !q.field.startsWith('bloque13_item'));
  }

  // ocultar bloque 14 completo si la respuesta AB de ese bloque es “No”
  if (answers[bloque14Key] === 'No') {
    qs = qs.filter(q => !q.field.startsWith('bloque14_item'));
  }

  return qs;
}, [allSecINo, mergedQuestions, answers]);

  const totalQuestions = effectiveQuestions.length;

  // 5) Ajusto el tamaño de questionsRef cuando cambia totalQuestions
  useEffect(() => {
    questionsRef.current = questionsRef.current.slice(0, totalQuestions);
  }, [totalQuestions]);

  // 6) Cargo el nombre de la sucursal y las áreas exclusivas de la aplicación.
  useEffect(() => {
    (async () => {
      setConfigurationLoaded(false);
      try {
        const scope = await questionnaireScopeApi.getById(idEmpresa);
        setCompanyName(scope.name);
      } catch (err) {
        console.error('Error cargando empresa/sucursal:', err);
      }
      try {
        if (!applicationId) {
          setCompanyJobAreas([]);
          setUsesEmployeeCredentials(false);
          return;
        }
        const config = await questionnaireApplicationsApi.getNom035Config(applicationId);
        setCompanyJobAreas(config.jobAreas || []);
        setUsesEmployeeCredentials(Boolean(config.usesEmployeeCredentials));
      } catch (err) {
        setCompanyJobAreas([]);
        setUsesEmployeeCredentials(false);
        console.error('Error cargando áreas de la aplicación NOM-035:', err);
      } finally {
        setConfigurationLoaded(true);
      }
    })();
  }, [idEmpresa, applicationId]);

  const markQuestionAsAnswered = useCallback((index, field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value ?? '' }));
    setAnsweredState(prev => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  }, []);

  const totalPages = Math.ceil(totalQuestions / PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(0);
  const start = currentPage * PAGE_SIZE;
  const pageQuestions = effectiveQuestions.slice(start, start + PAGE_SIZE);
  const progressPct = Math.round(((currentPage + 1) / totalPages) * 100);
  const isPageComplete = pageQuestions.every(q => {
    const idx = mergedQuestions.findIndex(x => x.field === q.field);
    return answeredState[idx];
  });

  const handleNext = async () => {
    if (usesEmployeeCredentials && currentPage === 0) {
      setValidatingEmployee(true);
      try {
        await questionnaireApplicationsApi.validateEmployeeCredentials(applicationId, {
          employee_number: answers.employee_number,
          employee_code: answers.employee_code,
        });
      } catch (err) {
        alert(err.response?.data?.message || "No se pudo validar al empleado.");
        return;
      } finally {
        setValidatingEmployee(false);
      }
    }
    setCurrentPage(Math.min(currentPage + 1, totalPages - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handlePrev = () => {
    setCurrentPage(Math.max(currentPage - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSend = async () => {
    // busco la primera no contestada
    const firstUnanswered = effectiveQuestions.findIndex(
      q => !answeredState[ mergedQuestions.findIndex(x => x.field === q.field) ]
    );
    if (firstUnanswered !== -1) {
      const globalIdx = mergedQuestions.findIndex(x => x.field === effectiveQuestions[firstUnanswered].field);
      const el = questionsRef.current[globalIdx];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const timestamp = new Date().toISOString();
    const payload = {
      companyId: idEmpresa,
      questionnaireName: questionnaire,
      answers,
      timestamp,
      applicationId: applicationId || null,
    };

    try {
      
      await responsesApi.createNom035Response(payload);
      navigate('/nom035/results');
    } catch (err) {
      const msg = err.response?.data?.message;
      alert(msg || 'Error al enviar respuestas.');
      console.error(err);
    }
  };

  if (!configurationLoaded) {
    return <div className="questionnaire-container">Cargando cuestionario...</div>;
  }

  return (
    <div className="questionnaire-container">
      <h2>Cuestionario NOM-035</h2>
      <p>Empresa: {companyName}</p>
      <p>Lea cuidadosamente cada enunciado y marque la respuesta correspondiente.</p>

      <div className="progress-fill" style={{ width: `${progressPct}%` }} />
      <p className="progress-text">
        Página {currentPage + 1} de {totalPages}
      </p>

      {pageQuestions.map((q, idx) => {
        const globalIdx = mergedQuestions.findIndex(x => x.field === q.field);
        const QuestionComp = q.Component;
        return (
          <div
            key={q.field}
            ref={el => (questionsRef.current[globalIdx] = el)}
            className="question-card"
          >
            <QuestionComp
              question={q.question}
              required={q.required}
              type={q.type}
              options={q.options}
              optionA={q.optionA}
              optionB={q.optionB}
              followUp={q.followUp}
              maxRating={q.maxRating}
              hourRange={q.hourRange}
              invertOrder={q.invertOrder}
              uniqueChoice={q.uniqueChoice}
              field={q.field}
              value={answers[q.field] ?? ""}
              updateProgress={val => markQuestionAsAnswered(globalIdx, q.field, val)}
            />
          </div>
        );
      })}

      <div className="pagination">
        {currentPage > 0 && (
          <button onClick={handlePrev} className="page-button">
            Anterior
          </button>
        )}
        {currentPage < totalPages - 1 ? (
          <button
            onClick={handleNext}
            className="page-button"
            disabled={!isPageComplete || validatingEmployee}
          >
            {validatingEmployee ? "Validando..." : "Siguiente"}
          </button>
        ) : (
          <button onClick={handleSend} className="page-button submit-button">
            Enviar
          </button>
        )}
      </div>
    </div>
  );
}
